import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Localization } from "@viamap/viamap2-common";
import {
  ADModalBody,
  AdvancedDragModal,
} from "src/componentsUtils/AdvancedDragModal";
import {
  closeWindow,
  ManagedWindow,
} from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import {
  MapitStateContext,
  type LayersType,
} from "src/states/MapitState";
import { LayerVariabilityType } from "src/common/managers/Types";
import { buffer, feature } from "@turf/turf";
import {
  addSearchAreaOutline,
  removeSearchAreaLayer,
  setSearchAreaGeometry,
} from "src/modules/JPBModule/JPBSearchArea";
import { JPBManager } from "src/modules/JPBModule/JPBManager";
import { JPBHistogram } from "src/modules/JPBModule/JPBHistogram";
import { JPBStatistics } from "src/modules/JPBModule/JPBStatistics";
import {
  JPBTable,
  type JPBTableRef,
} from "src/modules/JPBModule/JPBTable";
import {
  AreaUnit,
  AverageCalculationMethod,
  JPBPriceIndexSource,
  PriceMode,
  YearGrouping,
  type JPBAssumptions,
  type JPBReferenceProperty,
} from "src/modules/JPBModule/JPBState";
import {
  createInitialJPBSessionState,
  JPBSessionProvider,
  useJPBSessionActions,
  useJPBSessionSlice,
} from "src/modules/JPBModule/JPBSessionState";
import { JPBSkovDemoManager } from "./JPBSkovDemoManager";
import {
  buildSkovDemoNeutralAssumptions,
  initialJPBSkovDemoFilters,
  initialJPBSkovDemoResults,
} from "./JPBSkovDemoState";
import { JPBSkovDemoSearchArea } from "./JPBSkovDemoSearchArea";
import {
  highlightSkovPolygonOnMap,
  removeJPBSkovDemoMapLayers,
  setupJPBSkovDemoMapLayers,
  SKOV_SOURCE_ID,
  SKOV_SOURCE_ID_HOVER,
  SKOV_SOURCE_ID_POLY,
} from "./JPBSkovDemoMapLayers";
import "src/css/propertyInfoStyle.css";

const SEARCH_AREA_ID = "jpb-skov-demo-search-area";

function getDefaultBufferKm(
  selectedLayer: number | undefined,
  layers: LayersType,
): number {
  if (selectedLayer === undefined) return 10;
  const layer = layers[selectedLayer];
  if (!layer) return 10;
  return layer.type?.toLowerCase().includes("point") ? 10 : 0;
}

function JPBSkovDemoWindowInner(props: { managedWindow: ManagedWindow }) {
  const { dispatch: windowDispatch } = useWindowContext();
  const { state: mapitState } = useContext(MapitStateContext);

  const [filters, setFilters] = useState(initialJPBSkovDemoFilters);
  const [results, setResults] = useState(initialJPBSkovDemoResults);
  const [bufferGeometry, setBufferGeometry] = useState<any>(null);

  const [sessionFilters] = useJPBSessionSlice("filters");
  const { patchFilters } = useJPBSessionActions();

  const areaUnit = sessionFilters.areaUnit;
  const priceMode = sessionFilters.priceMode;
  const averageCalculationMethod = sessionFilters.averageCalculationMethod;
  const priceIndexSource = sessionFilters.priceIndexSource;
  const checkedItems = sessionFilters.checkedItems;
  const hideUnselected = sessionFilters.hideUnselected;
  const customPriceAssumptions = sessionFilters.customPriceAssumptions;
  const appliedYearGrouping = sessionFilters.appliedYearGrouping;
  const appliedCalculationDateTo = sessionFilters.appliedCalculationDateTo;

  const tableRef = useRef<JPBTableRef>(null);
  const tableScrollAnchorRef = useRef<HTMLDivElement>(null);
  const checkedItemsRef = useRef(checkedItems);
  checkedItemsRef.current = checkedItems;
  const highlightCleanupRef = useRef<(() => void) | null>(null);

  /** Skov index rows usually lack `overlapArea.markOverlap`; Landbrug unit price is then null and columns show "-". */
  useEffect(() => {
    if (priceMode !== PriceMode.Standard) {
      patchFilters({ priceMode: PriceMode.Standard });
    }
  }, [patchFilters, priceMode]);

  const selectableLayers = useMemo(() => {
    return Object.values(mapitState.layers).filter(
      (layer) =>
        layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer,
    );
  }, [mapitState.layers]);

  useEffect(() => {
    const map = mapitState.map?.getMapPop?.();
    if (!map) return;
    addSearchAreaOutline(map, SEARCH_AREA_ID, "#d6c251");
    return () => {
      removeSearchAreaLayer(map, SEARCH_AREA_ID);
    };
  }, [mapitState.map]);

  useEffect(() => {
    const selectedLayerId = filters.selectedLayerId;
    if (selectedLayerId === undefined) {
      setBufferGeometry(null);
      const map = mapitState.map?.getMapPop?.();
      if (map) {
        setSearchAreaGeometry(map, SEARCH_AREA_ID, null);
      }
      return;
    }

    const selectedLayer = mapitState.layers[selectedLayerId];
    const sourceGeometry = selectedLayer?.geoJson;
    if (!sourceGeometry) {
      setBufferGeometry(null);
      return;
    }

    const buffered = buffer(sourceGeometry as any, filters.bufferDistanceKm, {
      units: "kilometers",
    });
    const combined = JPBManager.toCombinedSearchGeometry(buffered) || buffered;
    setBufferGeometry(combined);
    const map = mapitState.map?.getMapPop?.();
    if (map) {
      setSearchAreaGeometry(map, SEARCH_AREA_ID, combined);
    }
  }, [
    filters.selectedLayerId,
    filters.bufferDistanceKm,
    mapitState.layers,
    mapitState.map,
  ]);

  useEffect(() => {
    const run = async () => {
      if (!bufferGeometry) {
        setResults((prev) => ({
          ...prev,
          loading: false,
          error: null,
          properties: [],
        }));
        return;
      }

      const polygonCoordinates =
        JPBManager.extractPolygonCoordinates(bufferGeometry);
      if (!polygonCoordinates) {
        setResults((prev) => ({
          ...prev,
          loading: false,
          properties: [],
          error: Localization.getText(
            "Could not extract polygon coordinates from buffer",
          ),
        }));
        return;
      }

      const abortController = new AbortController();
      setResults((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const properties = await JPBSkovDemoManager.fetchReferenceProperties({
          polygonCoordinates,
          abortSignal: abortController.signal,
        });
        if (abortController.signal.aborted) return;
        setResults({
          loading: false,
          error: null,
          properties,
        });
      } catch (error) {
        if (abortController.signal.aborted) return;
        setResults({
          loading: false,
          properties: [],
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };
    run();
  }, [bufferGeometry]);

  /** Demo: show every hit from Elasticsearch — no histogram or JPB criterion filtering. */
  const demoProperties = results.properties;

  const skovAssumptions = useMemo(
    () => buildSkovDemoNeutralAssumptions(demoProperties),
    [demoProperties],
  );

  const propertyIdsKey = useMemo(
    () =>
      demoProperties
        .map((p) => p.documentID)
        .sort()
        .join(","),
    [demoProperties],
  );

  const propertyIdsKeyRef = useRef<string>("");
  useEffect(() => {
    if (propertyIdsKeyRef.current === propertyIdsKey) return;
    propertyIdsKeyRef.current = propertyIdsKey;
    patchFilters({
      checkedItems: new Set(demoProperties.map((p) => p.documentID)),
      customPriceAssumptions: buildSkovDemoNeutralAssumptions(demoProperties),
    });
  }, [propertyIdsKey, demoProperties, patchFilters]);

  const lockedDocumentIds = useMemo(() => {
    const s = new Set<string>();
    for (const p of demoProperties) {
      const lock = JPBManager.getSelectionLockState(p, skovAssumptions);
      if (lock.isLocked) {
        s.add(p.documentID);
      }
    }
    return s;
  }, [demoProperties, skovAssumptions]);

  const tableDocumentIds = useMemo(
    () => new Set(demoProperties.map((p) => p.documentID)),
    [demoProperties],
  );

  const updateCheckedItems = useCallback(
    (next: Set<string>) => {
      patchFilters({ checkedItems: next });
    },
    [patchFilters],
  );

  const mapFacade = mapitState.map;

  const handleRowClick = useCallback(
    (property: JPBReferenceProperty) => {
      if (!mapFacade) {
        return;
      }
      const map = mapFacade.getMapPop();
      if (!map) {
        return;
      }

      if (highlightCleanupRef.current) {
        highlightCleanupRef.current();
        highlightCleanupRef.current = null;
      }

      if (property?.geoshape) {
        try {
          let geo: any = property.geoshape;
          if (typeof geo === "string") {
            try {
              geo = JSON.parse(geo);
            } catch {
              geo = null;
            }
          }
          if (geo) {
            const geoshapeFeature = feature(geo);
            mapFacade.zoomToDataAvoidingWindows(geoshapeFeature, 25);
            highlightCleanupRef.current = highlightSkovPolygonOnMap(
              map,
              geo,
              property.documentID,
              5000,
            );
          }
        } catch (error) {
          console.error("JPBSkovDemo handleRowClick polygon:", error);
        }
      } else if (
        Number.isFinite(property.coordinates?.lng) &&
        Number.isFinite(property.coordinates?.lat)
      ) {
        try {
          const pointFeature = feature({
            type: "Point",
            coordinates: [property.coordinates.lng, property.coordinates.lat],
          });
          const bufferedFeature = buffer(pointFeature, 0.5, {
            units: "kilometers",
          });
          if (bufferedFeature) {
            mapFacade.zoomToDataAvoidingWindows(bufferedFeature, 25);
            highlightCleanupRef.current = highlightSkovPolygonOnMap(
              map,
              bufferedFeature.geometry,
              property.documentID,
              5000,
            );
          }
        } catch (error) {
          console.error("JPBSkovDemo handleRowClick point:", error);
        }
      }
    },
    [mapFacade],
  );

  const handleHideUnselectedChange = useCallback(
    (mode: typeof hideUnselected) => {
      patchFilters({ hideUnselected: mode });
    },
    [patchFilters, hideUnselected],
  );

  const handleCustomAssumptionsChange = useCallback(
    (patch: Partial<JPBAssumptions>) => {
      const base = buildSkovDemoNeutralAssumptions(demoProperties);
      patchFilters({
        customPriceAssumptions: {
          pricePerMeterMin: patch.pricePerMeterMin ?? base.pricePerMeterMin,
          pricePerMeterMax: patch.pricePerMeterMax ?? base.pricePerMeterMax,
          areaMin: patch.areaMin ?? base.areaMin,
          areaMax: patch.areaMax ?? base.areaMax,
          effectiveMarkOverlapThreshold: -1,
          excludeBuildings: patch.excludeBuildings ?? base.excludeBuildings,
        },
      });
    },
    [demoProperties, patchFilters],
  );

  useEffect(() => {
    const mapPop = mapFacade?.getMapPop?.();
    if (!mapPop || demoProperties.length === 0) {
      removeJPBSkovDemoMapLayers(mapPop ?? null);
      return;
    }

    const cleanup = setupJPBSkovDemoMapLayers(
      mapPop,
      demoProperties,
      checkedItemsRef.current,
      PriceMode.Standard,
      areaUnit,
      {
        onFeatureClick: (documentID: string, ctrlKey: boolean) => {
          if (!ctrlKey) {
            if (tableRef.current && tableDocumentIds.has(documentID)) {
              tableScrollAnchorRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
              tableRef.current.scrollToRow(documentID);
            }
          } else {
            if (lockedDocumentIds.has(documentID)) {
              return;
            }
            const map = mapPop;
            const featureState = map.getFeatureState({
              source: SKOV_SOURCE_ID,
              id: documentID,
            });
            const currentChecked = featureState?.checked === true;
            const newChecked = !currentChecked;
            const newCheckedItems = new Set(checkedItemsRef.current);
            if (newChecked) {
              newCheckedItems.add(documentID);
            } else {
              newCheckedItems.delete(documentID);
            }
            updateCheckedItems(newCheckedItems);
          }
        },
      },
      lockedDocumentIds,
      skovAssumptions,
    );

    return cleanup;
  }, [
    mapFacade,
    demoProperties,
    areaUnit,
    lockedDocumentIds,
    skovAssumptions,
    tableDocumentIds,
    updateCheckedItems,
  ]);

  useEffect(() => {
    const mapPop = mapFacade?.getMapPop?.();
    if (!mapPop) return;
    for (const p of demoProperties) {
      if (!p.documentID) continue;
      const isChecked = checkedItems.has(p.documentID);
      mapPop.setFeatureState(
        { source: SKOV_SOURCE_ID, id: p.documentID },
        { checked: isChecked, hover: false },
      );
      if (mapPop.getSource(SKOV_SOURCE_ID_HOVER)) {
        mapPop.setFeatureState(
          { source: SKOV_SOURCE_ID_HOVER, id: p.documentID },
          { checked: isChecked, hover: false },
        );
      }
      mapPop.setFeatureState(
        { source: SKOV_SOURCE_ID_POLY, id: p.documentID },
        { checked: isChecked, hover: false },
      );
    }
  }, [mapFacade, demoProperties, checkedItems]);

  const indexMaxTransactionDate = useMemo(() => {
    let max: Date | undefined;
    for (const p of results.properties) {
      const d = p.transactionDate;
      if (d instanceof Date && !Number.isNaN(d.getTime())) {
        if (!max || d > max) max = d;
      }
    }
    return max;
  }, [results.properties]);

  return (
    <AdvancedDragModal
      title={Localization.getText("jpbSkovDemo:title")}
      window={props.managedWindow}
      allowUserMiniMize={true}
      allowPopout={true}
      allowFullscreen={true}
      className="JPBModule jpb-view-advanced"
      PosDefault={{
        top: "12px",
        left: "calc(100% - 12px)",
        transform: "translateX(-100%)",
        width: "1100px",
      }}
      PositionToSave={{ left: true, top: true, width: true, height: true }}
      onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
      subHeaderContent={
        <JPBSkovDemoSearchArea
          selectableLayers={selectableLayers}
          selectedLayerId={filters.selectedLayerId}
          bufferDistanceKm={filters.bufferDistanceKm}
          areaUnit={areaUnit}
          averageCalculationMethod={averageCalculationMethod}
          onLayerChange={(layerId) => {
            setFilters((prev) => ({
              ...prev,
              selectedLayerId: layerId,
              bufferDistanceKm: getDefaultBufferKm(
                layerId,
                mapitState.layers,
              ),
            }));
          }}
          onBufferDistanceChange={(distanceKm) =>
            setFilters((prev) => ({ ...prev, bufferDistanceKm: distanceKm }))
          }
          onAreaUnitChange={(next) => patchFilters({ areaUnit: next })}
          onAverageCalculationMethodChange={(next) =>
            patchFilters({ averageCalculationMethod: next })
          }
        />
      }
    >
      <ADModalBody>
        {results.loading ? (
          <div className="jpb-card jpb-card--padded">
            {Localization.getText("jpbSkovDemo:loadingData")}
          </div>
        ) : null}
        {results.error ? (
          <div className="jpb-card jpb-card--padded" style={{ color: "#a94442" }}>
            {Localization.getFormattedText("jpbSkovDemo:fetchError", {
              error: results.error,
            })}
          </div>
        ) : null}

        <div className="jpb-grid jpb-grid--advanced">
          <div className="jpb-grid-main">
            <JPBHistogram
              data={demoProperties}
              allData={demoProperties}
              areaUnit={areaUnit}
              priceMode={PriceMode.Standard}
              averageCalculationMethod={averageCalculationMethod}
            />
            {demoProperties.length > 0 && checkedItems.size > 0 ? (
              <JPBStatistics
                data={demoProperties}
                areaUnit={areaUnit}
                priceMode={PriceMode.Standard}
                checkedItems={checkedItems}
                yearGrouping={appliedYearGrouping}
                calculationDateTo={appliedCalculationDateTo ?? undefined}
                indexMaxTransactionDate={indexMaxTransactionDate}
                averageCalculationMethod={averageCalculationMethod}
                priceIndexSource={priceIndexSource}
              />
            ) : null}
            {demoProperties.length > 0 ? (
              <>
                <div ref={tableScrollAnchorRef} />
                <JPBTable
                  ref={tableRef}
                  data={demoProperties}
                  checkedItems={checkedItems}
                  onCheckedItemsChange={updateCheckedItems}
                  onRowClick={handleRowClick}
                  yearGrouping={appliedYearGrouping}
                  averageCalculationMethod={averageCalculationMethod}
                  calculationDateTo={appliedCalculationDateTo ?? undefined}
                  indexMaxTransactionDate={indexMaxTransactionDate}
                  hideUnselected={hideUnselected}
                  onHideUnselectedChange={handleHideUnselectedChange}
                  customAssumptions={
                    customPriceAssumptions ?? skovAssumptions
                  }
                  onCustomAssumptionsChange={handleCustomAssumptionsChange}
                  areaUnit={areaUnit}
                  priceMode={PriceMode.Standard}
                  priceIndexSource={priceIndexSource}
                  showAlternateUnitPriceColumn={true}
                />
              </>
            ) : null}
          </div>
          <div className="jpb-grid-sidebar">
            <div className="jpb-card jpb-card--padded">
              <div style={{ fontWeight: 600 }}>
                {Localization.getText("jpbSkovDemo:transactionsHeading")}
              </div>
              <div>
                {Localization.getFormattedText(
                  "jpbSkovDemo:showingFiltered",
                  {
                    shown: demoProperties.length,
                    total: results.properties.length,
                  },
                )}
              </div>
            </div>
          </div>
        </div>
      </ADModalBody>
    </AdvancedDragModal>
  );
}

const MemoJPBSkovDemoWindowInner = memo(JPBSkovDemoWindowInner);

export function JPBSkovDemoWindow(props: { managedWindow: ManagedWindow }) {
  const initialSessionState = useMemo(
    () =>
      createInitialJPBSessionState({
        priceIndexSource: JPBPriceIndexSource.None,
        areaUnit:
          (localStorage.getItem("jpb.areaUnit") as AreaUnit) ||
          AreaUnit.SquareMeter,
        priceMode:
          (localStorage.getItem("jpb.priceMode") as PriceMode) ||
          PriceMode.Standard,
        averageCalculationMethod:
          (localStorage.getItem(
            "jpb.averageCalculationMethod",
          ) as AverageCalculationMethod) ||
          AverageCalculationMethod.WeightedByArea,
        viewMode: "advanced",
      }),
    [],
  );
  const sessionKey = `skov-demo-${props.managedWindow.uid ?? props.managedWindow.id}`;
  return (
    <JPBSessionProvider
      sessionKey={sessionKey}
      initialState={{
        ...initialSessionState,
        filters: {
          ...initialSessionState.filters,
          appliedYearGrouping: YearGrouping.ThreeYears,
          priceMode: PriceMode.Standard,
        },
        tableUi: {
          ...initialSessionState.tableUi,
          visibleColumns: Array.from(
            new Set([
              ...initialSessionState.tableUi.visibleColumns,
              "documentIDAction",
              "priceAlternateAreaUnit",
            ]),
          ),
        },
      }}
    >
      <MemoJPBSkovDemoWindowInner managedWindow={props.managedWindow} />
    </JPBSessionProvider>
  );
}
